/**
 * Admin → User Activity: the data model.
 *
 * Everything the screen knows about the shape of
 * `GET /auth/admin/user-activity` lives here — the metric vocabulary, the two
 * folds (by person / by calendar day), the automation test and the date
 * helpers. The screen and its cell renderers import from this; nothing here
 * touches React.
 *
 * Contract: ../eastview-os/frontend_documentation/user-activity-view.md
 */
import { amber, slate, status, steel, teal } from "../../../theme/colors";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import {
  convertToEasternTime,
  normalizeIsoStringToUtc,
} from "../../../_src_shared/utils/functions/formatDateTime";

/**
 * The Service Bus topics that ARE condition work. The backend's
 * `activity_category` has no `conditions` bucket, so every one of these lands
 * in the `data_updates` catch-all — but the raw `event_types` map the same
 * response carries is keyed by topic, so the count is recoverable without a
 * backend change. Data edits then means data edits: `data_updates` minus these.
 *
 * (Checklist `task_updates` / `acquisition_task_lists` are deliberately NOT
 * here — tasks are not conditions, and they stay in the data-edit catch-all.)
 */
export const CONDITION_TOPICS = [
  "underwriting_conditions",
  "acquisition_underwriting_conditions",
  "acquisition_conditions_and_stips",
];

export const num = (v) => Number(v) || 0;
const cat = (r, k) => num(r?.categories?.[k]);
const conditionsOf = (r) =>
  CONDITION_TOPICS.reduce((n, t) => n + num(r?.event_types?.[t]), 0);

/**
 * Automation vs a person.
 *
 * The backend only labels an actor `System` when `changed_by` came through
 * empty, so every named service account arrives as itself and would otherwise
 * sit in the grid masquerading as a person — and, worse, survive the "Include
 * automation" switch. Three rules, in order of how sure they are:
 *
 *   1. the literal `System` the backend assigns;
 *   2. an actor with no `@` at all — `box_sync`, `salesforce_sync`;
 *   3. a real mailbox whose local part names a robot — `automation@…`,
 *      `*_sync@…`, `noreply@…`. EV Automations
 *      (`automation@eastviewinv.com`) is the one that made this rule
 *      necessary: it IS a valid address, so (2) let it through.
 *
 * Rule 3 is the guess. If the desk has a canonical service-account list, put it
 * in `SYSTEM_ACCOUNTS` and the guessing stops.
 */
const SYSTEM_ACCOUNTS = new Set(["automation@eastviewinv.com"]);
const SYSTEM_LOCAL_PARTS = new Set([
  "automation",
  "noreply",
  "no-reply",
  "system",
  "service",
]);

export const isSystem = (r) => {
  const u = (r?.user || "").toLowerCase().trim();
  if (!u) return false;
  if (u === "system" || SYSTEM_ACCOUNTS.has(u)) return true;
  if (!u.includes("@")) return true;
  const local = u.split("@")[0];
  return (
    SYSTEM_LOCAL_PARTS.has(local) ||
    local.endsWith("_sync") ||
    local.endsWith("-sync")
  );
};

/**
 * The five edit categories as a drawable series, plus the reads that trail
 * them. Colors are the product's, not a chart library's: brand teal for the
 * bulk of the work, the second accent (steel) and the two status hues for the
 * smaller slices, and the neutral for reads — which is the point, since reads
 * are the thing being visually demoted.
 *
 * Steel sits BETWEEN the two teals in the stack on purpose: the two teal steps
 * are the closest pair in the set, so nothing puts them side by side.
 */
export const SERIES = [
  { key: "dataEdits", label: "Data", color: teal[500] },
  { key: "conditions", label: "Conditions", color: steel.main },
  { key: "comments", label: "Comments", color: teal[700] },
  { key: "exceptions", label: "Exceptions", color: amber.main },
  { key: "creations", label: "Creations", color: status.success.solid },
];

export const VIEW_SERIES = { label: "Views", color: slate[300] };

/**
 * One served row flattened to the metric vocabulary every view shares. `edits`
 * and `views` are the backend's read/write split; the rest are the categories,
 * with conditions lifted out of the catch-all.
 */
export const metricsOf = (r) => {
  const conditions = conditionsOf(r);
  return {
    edits: num(r.write_events),
    dataEdits: Math.max(0, cat(r, "data_updates") - conditions),
    comments: cat(r, "comments"),
    conditions,
    exceptions: cat(r, "exceptions"),
    creations: cat(r, "loan_creations"),
    loanViews: cat(r, "loan_views"),
    docViews: cat(r, "document_views"),
    views: num(r.view_events),
    loans: num(r.loan_count),
    total: num(r.total_events),
  };
};

// Everything summable. `loans` is NOT in here: loan_count is distinct-per-row,
// so adding it up double-counts a loan touched twice — the rolled-up figure
// comes from the union of `loans_touched` instead.
export const SUM_KEYS = [
  "edits",
  "dataEdits",
  "comments",
  "conditions",
  "exceptions",
  "creations",
  "loanViews",
  "docViews",
  "views",
  "total",
];

const addInto = (acc, r) => {
  SUM_KEYS.forEach((k) => {
    acc[k] += r[k];
  });
  (r.loans_touched || []).forEach((l) => acc.loanSet.add(l));
};

const zeroed = (base) => {
  const a = { ...base, loanSet: new Set() };
  SUM_KEYS.forEach((k) => {
    a[k] = 0;
  });
  return a;
};

// `loans_touched` should always be there; fall back to the widest single child
// rather than reporting zero if it ever isn't.
const distinctLoans = (acc, children) =>
  acc.loanSet.size || Math.max(0, ...children.map((c) => c.loans), 0);

/**
 * Fold the user×day rows into one cumulative row per PERSON. People first,
 * automation grouped after them — a service account with 3,000 events would
 * otherwise sit at the top of a list about who is working.
 */
export const rollupByUser = (dayRows) => {
  const byUser = new Map();

  dayRows.forEach((r) => {
    const key = r.user || "System";
    let a = byUser.get(key);
    if (!a) {
      a = zeroed({
        user: key,
        userName: r.userName,
        sys: isSystem(r),
        activeDays: 0,
        days: [],
      });
      byUser.set(key, a);
    }
    a.activeDays += 1;
    addInto(a, r);
    a.days.push(r);
  });

  const list = [...byUser.values()]
    .map((a) => ({
      ...a,
      loans: distinctLoans(a, a.days),
      days: [...a.days].sort((x, y) =>
        String(y.date).localeCompare(String(x.date)),
      ),
    }))
    .sort((a, b) => b.edits - a.edits || b.total - a.total);

  return [...list.filter((u) => !u.sys), ...list.filter((u) => u.sys)];
};

/** Fold the user×day rows into one row per CALENDAR DAY. */
export const rollupByDay = (dayRows) => {
  const byDate = new Map();

  dayRows.forEach((r) => {
    const key = r.date || "—";
    let a = byDate.get(key);
    if (!a) {
      a = zeroed({ date: key, userSet: new Set(), systemCount: 0, entries: [] });
      byDate.set(key, a);
    }
    if (isSystem(r)) a.systemCount += 1;
    else a.userSet.add(r.user);
    addInto(a, r);
    a.entries.push(r);
  });

  return [...byDate.values()]
    .map((a) => ({
      ...a,
      activeUsers: a.userSet.size,
      loans: distinctLoans(a, a.entries),
      // Busiest person first inside the day.
      entries: [...a.entries].sort(
        (x, y) => y.edits - x.edits || y.total - x.total,
      ),
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

/** Window-level KPIs for the stat strip. People and automation kept apart. */
export const summarize = (dayRows) => {
  const people = dayRows.filter((r) => !isSystem(r));
  const sys = dayRows.filter((r) => isSystem(r));
  const sum = (arr, k) => arr.reduce((n, r) => n + num(r[k]), 0);
  return {
    activeUsers: new Set(people.map((r) => r.user)).size,
    edits: sum(people, "edits"),
    views: sum(people, "views"),
    // Deliberately the SUM of the daily distincts, not a window-wide union:
    // "how many loans got worked on today, summed over the window" is the
    // workload question. The per-row Loans column is the union.
    loanTouches: sum(people, "loans"),
    systemEvents: sum(sys, "edits"),
  };
};

// ── dates: TWO different days, and they are not the same day ────────────────
/**
 * The backend's `date` is a **UTC calendar day**. Every audit timestamp is
 * written as `dt.datetime.now().isoformat()` on a UTC-clocked server (naive, no
 * offset), and `aggregate_user_activity` buckets on `ts[:10]` — the UTC date.
 *
 * So a UTC day D is NOT the desk's day D. In EDT it runs
 * **D-1 8:00 PM → D 8:00 PM Eastern**, which has two consequences the screen
 * has to handle rather than paper over:
 *
 *   1. Evening work rolls forward. Anything a person does after 8 PM Eastern
 *      lands in the NEXT UTC day's row — which is why, at 10:30 PM on Aug 27,
 *      a bucket dated "Aug 28" exists and looks like tomorrow.
 *   2. "Today" is ambiguous. Comparing a UTC bucket against the BROWSER's local
 *      date is simply a category error, and produced exactly that: the newest
 *      bucket unlabelled and the previous one called "Today".
 *
 * Hence two families below. The grids label the bucket they actually show, in
 * UTC (`relativeDayUtc`). The Timeline regroups to real Eastern days
 * (`easternDateOf`), because an intra-day clock is meaningless otherwise — it
 * was drawing last night's 9 PM session on today's strip.
 */
const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** The UTC calendar date `offset` days back — the backend's own reckoning. */
const utcDayIso = (offset = 0) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
};

/** The Eastern calendar date of a timestamp — the desk's reckoning. */
export const easternDateOf = (isoString) => {
  const d = convertToEasternTime(normalizeIsoStringToUtc(String(isoString)));
  if (!d || Number.isNaN(d.getTime())) return null;
  return isoOf(d);
};

export const easternTodayIso = () => easternDateOf(new Date().toISOString());

/** The calendar day after a `YYYY-MM-DD`, without tripping over a month end. */
export const nextDayIso = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
};

/** Today/Yesterday for a UTC bucket — what the GRIDS show. */
export const relativeDayUtc = (iso) => {
  if (iso === utcDayIso(0)) return "Today";
  if (iso === utcDayIso(1)) return "Yesterday";
  return null;
};

/** The calendar day before a `YYYY-MM-DD`. */
export const prevDayIso = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
};

/** Today/Yesterday for a real Eastern day — what the TIMELINE shows. */
export const relativeDayEastern = (iso) => {
  const today = easternTodayIso();
  if (iso === today) return "Today";
  if (iso === prevDayIso(today)) return "Yesterday";
  return null;
};

export const longDay = (iso) => formatDate(iso, "—", "EEE, MMM d");
export const shortDay = (iso) => formatDate(iso, "—", "MMM d");
export const fullDate = (iso) => formatDate(iso, "—", "MMM d, yyyy");


// ── session timeline ────────────────────────────────────────────────────────
/**
 * Turning raw audit events into the day's work sessions.
 *
 * The events come from `GET /underwriting/loan_events/{dealId}` (see
 * useUserActivitySessions.js for the join). Each `by_topic` record carries
 * `timestamp`, `changed_by`, `event_type` and `sub_event` — everything needed
 * to place it on a clock and label what kind of work it was.
 */

/** A break longer than this ends a session and starts the next one. */
export const IDLE_GAP_MIN = 15;

const DOC_VIEW_SUB_EVENTS = [
  "view_file",
  "download_loan_docs",
  "download_document_package",
];

/**
 * A JS mirror of `app/services/user_activity.py::activity_category`. It has to
 * stay in step with the backend's, or a reconstructed session would categorise
 * an event differently from the row it sits under. Any change there is a change
 * here.
 *
 * ONE deliberate addition: `conditions`. The backend has no such bucket and
 * folds condition topics into `data_updates`; the grid recovers them from the
 * `event_types` tally (§5.7) and the timeline can do better — it holds the raw
 * event, so it reads the topic directly. Still a WRITE either way, so the
 * read/write split is untouched and the two surfaces agree.
 */
export const categoryOfEvent = (ev) => {
  const type = ev?.event_type || "";
  const sub = ev?.sub_event || "";
  if (type === "loan_view") return "loan_views";
  if (CONDITION_TOPICS.includes(type)) return "conditions";
  if (type === "loan_documents" || type === "acquisition_loan_documents") {
    return DOC_VIEW_SUB_EVENTS.some((p) => sub.startsWith(p))
      ? "document_views"
      : "data_updates";
  }
  if (type === "comments" || type === "acquisition_comments") return "comments";
  if (type === "exception_updates") return "exceptions";
  if (["loan_creation", "create_loan", "acquisition_loan_creation"].includes(type))
    return "loan_creations";
  return "data_updates";
};

export const isViewCategory = (c) =>
  c === "loan_views" || c === "document_views";

/**
 * Keep the events one person generated on one EASTERN day.
 *
 * Deliberately NOT the backend's `ts[:10]` rule. That slice is the UTC date, so
 * filtering by it puts a 9 PM Eastern session on the following day's strip —
 * the bug this replaced. A clock is only readable against the day the person
 * actually lived, so the timeline regroups to Eastern and pays for it by
 * sourcing loans from two adjacent UTC rows (see useUserActivitySessions).
 */
export const eventsOnEasternDay = (events, user, easternDate) => {
  const who = String(user || "").toLowerCase();
  return (events || [])
    .filter((ev) => {
      const ts = typeof ev?.timestamp === "string" ? ev.timestamp : "";
      if (!ts || easternDateOf(ts) !== easternDate) return false;
      return String(ev.changed_by || "").toLowerCase() === who;
    })
    .map((ev) => ({
      ts: ev.timestamp,
      hour: hourOfDay(ev.timestamp),
      category: categoryOfEvent(ev),
      // Stamped by the fetch (the per-loan records don't name their own deal),
      // and resolved against the pipeline so a session can LINK its loans, not
      // just count them.
      dealId: ev.dealId ?? null,
      loanNumber: ev.loanNumber ?? null,
      loanName: ev.loanName ?? null,
      // Kept so a session can be opened up and show WHAT changed, not just how
      // much. `changed_fields` is the diff (or, on live-sink records, the
      // submitted body) — see `fieldChange` for the two shapes.
      eventType: ev.event_type || "",
      subEvent: ev.sub_event || "",
      changedFields: ev.changed_fields || null,
      statusChange: ev.status_change ?? null,
    }))
    .filter((e) => e.hour !== null)
    .sort((a, b) => a.hour - b.hour);
};

/**
 * Where on the clock an event sits, as a fractional hour in EASTERN time —
 * the zone the desk works in, and the one `formatDateTime` pins the rest of the
 * app to. A bare `new Date(ts).getHours()` would read the VIEWER's zone, which
 * would move everyone's workday depending on who opened the screen.
 */
export const hourOfDay = (isoString) => {
  const eastern = convertToEasternTime(normalizeIsoStringToUtc(String(isoString)));
  if (!eastern || Number.isNaN(eastern.getTime())) return null;
  return eastern.getHours() + eastern.getMinutes() / 60 + eastern.getSeconds() / 3600;
};

/**
 * Split a day's events into bursts: walk them in order and start a new session
 * wherever the person went quiet for longer than IDLE_GAP_MIN. This is the
 * whole point of the view — the gaps are the finding, not the blocks.
 */
export const buildSessions = (events) => {
  const out = [];
  let cur = null;

  events.forEach((e) => {
    if (!cur || (e.hour - cur.end) * 60 > IDLE_GAP_MIN) {
      cur = {
        start: e.hour,
        end: e.hour,
        edits: 0,
        views: 0,
        events: 0,
        // The events themselves, so the session can be expanded into "what
        // changed, on which loan".
        items: [],
        // Per-category tally so the session can explain WHAT the edits were,
        // not just how many — same vocabulary as the grid's columns.
        categories: {},
        // Keyed by dealId: the same loan touched twenty times is one loan, and
        // the id is what makes it openable.
        loanMap: new Map(),
      };
      out.push(cur);
    }
    cur.end = e.hour;
    cur.events += 1;
    cur.items.push(e);
    if (isViewCategory(e.category)) cur.views += 1;
    else cur.edits += 1;
    cur.categories[e.category] = (cur.categories[e.category] || 0) + 1;
    const id = e.dealId || e.loanNumber;
    if (id && !cur.loanMap.has(id)) {
      cur.loanMap.set(id, {
        dealId: e.dealId || null,
        loanNumber: e.loanNumber ?? null,
        loanName: e.loanName ?? null,
      });
    }
  });

  return out.map((s) => ({
    start: s.start,
    // A burst of one event has zero duration and would draw as nothing; give it
    // a minute so it's still visible on the strip as a touch.
    end: Math.max(s.end, s.start + 1 / 60),
    edits: s.edits,
    views: s.views,
    events: s.events,
    categories: s.categories,
    items: s.items,
    loanList: [...s.loanMap.values()],
    loans: s.loanMap.size,
  }));
};

/**
 * One field's change, out of the two shapes `changed_fields` comes in.
 *
 * The retroactive writer stores a real diff — `{prior, updated}` — while the
 * LIVE listener sink stores the submitted request body, i.e. the NEW value
 * only (`_summarize_changed_fields` in structured_audit.py). So "before" is
 * available for some events and genuinely unknown for others; this returns
 * `hasPrior` so the UI can say "set to X" rather than invent an empty before.
 */
export const fieldChange = (value) => {
  const isPair =
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("prior" in value || "updated" in value);
  return isPair
    ? { hasPrior: true, from: value.prior, to: value.updated }
    : { hasPrior: false, from: undefined, to: value };
};

/**
 * A session's edit categories mapped onto SERIES, so the hover card in the
 * timeline speaks the same language (and wears the same colours) as the grid's
 * Breakdown bar. `data_updates` minus conditions, exactly as `metricsOf` does
 * it for a rollup row.
 */
export const sessionEditMix = (session) => {
  const c = session?.categories || {};
  const conditions = num(c.conditions);
  const byKey = {
    dataEdits: Math.max(0, num(c.data_updates) - conditions),
    conditions,
    comments: num(c.comments),
    exceptions: num(c.exceptions),
    creations: num(c.loan_creations),
  };
  return SERIES.map((s) => ({ ...s, value: num(byKey[s.key]) }));
};

/** 9.75 → "9:45 AM". Sessions are hours-of-day, so no Date parsing is involved. */
export const formatHour = (h) => {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm % 60).padStart(2, "0")} ${ampm}`;
};

/** 1.75 → "1 h 45 m"; 0.2 → "12 min". */
export const formatDuration = (hours) => {
  const m = Math.max(1, Math.round(hours * 60));
  return m >= 60
    ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} m`
    : `${m} min`;
};
